'use client';
import { useState } from 'react';
import {
  ArrowUpRight,
  Info,
  LockOpen,
  Rocket,
  ThumbsUp,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { AppLink as Link } from '@curio/client';
import type { Account } from '@curio/contracts';
import { Button, Dialog } from '@curio/ui';
import { levelProgress, reviewerProgress, reviewerTiers } from '@curio/domain';
import { LevelRing } from '../components/level-ring';
import { ReviewerIcon } from '../components/reviewer-badge';
import { useReputation } from './reputation';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function ContributionStats({
  account,
  reviewHref = '#review-suggestions',
}: {
  account: Account;
  reviewHref?: string;
}) {
  const reputation = useReputation();
  const xp = reputation?.xp ?? account.xp;
  const rules = account.rules;
  const progress = levelProgress(xp, rules);
  const stats = account.reviewerStats;
  const tier = reviewerProgress(stats.acceptedReviews);
  const nextLevel = progress.level + 1;
  const toNextLevel =
    progress.remainingReviews !== null
      ? `${progress.remainingReviews} more accepted ${progress.remainingReviews === 1 ? 'review' : 'reviews'} to Level ${nextLevel}.`
      : `${progress.remainingXp} XP to Level ${nextLevel}.`;
  return (
    <section
      className={`panel contribution reviewer-${tier.current?.key ?? 'new'}`}
      aria-label="Review stats"
    >
      <div className="contribution-head">
        <LevelRing
          level={progress.level}
          xp={xp}
          step={progress.step}
          size={46}
          stroke={4}
        />
        <div className="contribution-title">
          <h2>
            Level {progress.level}
            <LevelLadder account={account} level={progress.level} />
          </h2>
          <p>{toNextLevel}</p>
        </div>
        <div className="contribution-aside">
          <Link className="reviewer-cta" href={reviewHref}>
            Find a product to review{' '}
            <ArrowUpRight size={14} aria-hidden="true" />
          </Link>
          <p>
            Each accepted review earns <strong>+{rules.reviewXp} XP</strong> and
            1 boost credit. Helpful reviews earn 2× XP.
          </p>
        </div>
      </div>
      <div className="stat-tiles">
        <div className="stat-tile stat-tile-xp">
          <span className="stat-tile-icon" aria-hidden="true">
            <Zap size={20} />
          </span>
          <div className="stat-tile-body">
            <strong className="stat-tile-value">
              {xp.toLocaleString()} XP
            </strong>
            <span className="stat-tile-label">
              from {plural(stats.acceptedReviews, 'review')}
            </span>
            <span className="stat-tile-bar" aria-hidden="true">
              <span style={{ width: `${progress.percent}%` }} />
            </span>
          </div>
        </div>
        <div className="stat-tile stat-tile-tier">
          <span className="stat-tile-icon" aria-hidden="true">
            {tier.current || tier.next ? (
              <ReviewerIcon tier={(tier.current ?? tier.next)!.key} size={20} />
            ) : null}
          </span>
          <div className="stat-tile-body">
            <strong className="stat-tile-value stat-tile-text">
              {tier.current ? `${tier.current.label} badge` : 'No badge yet'}
            </strong>
            <span className="stat-tile-label">
              {tier.next
                ? `${tier.next.label} at ${tier.next.minimum} accepted reviews`
                : 'Highest badge reached'}
            </span>
            {tier.next && (
              <span
                className="stat-tile-bar"
                role="progressbar"
                aria-label={`Progress to the ${tier.next.label} badge`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(tier.percent)}
                aria-valuetext={`${tier.remaining} more accepted ${tier.remaining === 1 ? 'review' : 'reviews'} to ${tier.next.label}`}
              >
                <span style={{ width: `${tier.percent}%` }} />
              </span>
            )}
          </div>
        </div>
        <div className="stat-tile stat-tile-helpful">
          <span className="stat-tile-icon" aria-hidden="true">
            <ThumbsUp size={20} />
          </span>
          <div className="stat-tile-body">
            <strong className="stat-tile-value">
              {stats.helpfulReviews.toLocaleString()}
            </strong>
            <span className="stat-tile-label">Marked helpful</span>
          </div>
        </div>
        <div className="stat-tile stat-tile-credits">
          <span className="stat-tile-icon" aria-hidden="true">
            <Rocket size={20} />
          </span>
          <div className="stat-tile-body">
            <strong className="stat-tile-value">
              {account.credits.toLocaleString()}
            </strong>
            <span className="stat-tile-label">
              Boost {account.credits === 1 ? 'credit' : 'credits'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function LevelLadder({ account, level }: { account: Account; level: number }) {
  const [open, setOpen] = useState(false);
  const rules = account.rules;
  const accepted = account.reviewerStats.acceptedReviews;
  const current = reviewerProgress(accepted).current;
  const reviewsPerLevel =
    rules.reviewXp > 0 ? Math.ceil(rules.levelXp / rules.reviewXp) : null;
  const bonusPerLevel = Math.round(rules.levelBonus * 100);
  const bonusCap = Math.round(rules.levelBonusCap * 100);
  const milestones: {
    key: string;
    reviews: number;
    done: boolean;
    reward: React.ReactNode;
    icon: React.ReactNode;
    tier?: (typeof reviewerTiers)[number];
  }[] = [
    {
      key: 'reward',
      reviews: 1,
      done: accepted >= 1,
      icon: <Zap size={13} />,
      reward: `+${rules.reviewXp} XP and 1 boost credit for every accepted review`,
    },
    {
      key: 'unlock',
      reviews: 2,
      done: account.unlocked,
      icon: <LockOpen size={13} />,
      reward: 'Submit and claim your own products',
    },
    ...(reviewsPerLevel
      ? [
          {
            key: 'ranking',
            reviews: reviewsPerLevel,
            done: level >= 2,
            icon: <TrendingUp size={13} />,
            reward: `Level 2 · +${bonusPerLevel}% discovery ranking for your products, then +${bonusPerLevel}% per level up to ${bonusCap}%`,
          },
        ]
      : []),
    ...reviewerTiers.map((tier) => ({
      key: tier.key,
      reviews: tier.minimum,
      done: accepted >= tier.minimum,
      tier,
      icon: <ReviewerIcon tier={tier.key} size={13} />,
      reward: (
        <>
          <span className={`milestone-tier reviewer-${tier.key}`}>
            <ReviewerIcon tier={tier.key} size={14} />
            {tier.label} badge
          </span>{' '}
          on your public reviews
        </>
      ),
    })),
  ].sort((a, b) => a.reviews - b.reviews);
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="contribution-info"
        aria-label="How levels and badges work"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <Info size={16} aria-hidden="true" />
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="How levels and badges work"
      >
        <ol className="level-ladder">
          {milestones.map((m) => (
            <li
              key={m.key}
              className={`${m.done ? 'milestone-done' : ''} ${m.tier ? `reviewer-${m.tier.key}` : ''}`}
              aria-current={
                m.tier && current?.key === m.tier.key ? 'step' : undefined
              }
            >
              <span className="milestone-mark" aria-hidden="true">
                {m.icon}
              </span>
              <span className="milestone-at">
                {plural(m.reviews, 'review')}
              </span>
              <span className="milestone-reward">{m.reward}</span>
              {m.tier && current?.key === m.tier.key && (
                <span className="reviewer-current">Your badge</span>
              )}
            </li>
          ))}
        </ol>
      </Dialog>
    </>
  );
}
